import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Button } from './ui/button';
import { BREEDS } from '../data/mock';
import { litterService } from '../services/litterApi';
import { X, Upload, Loader2 } from 'lucide-react';

const compressImage = (file, maxWidth = 800, quality = 0.5) => {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;
        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        canvas.toBlob((blob) => {
          resolve(new File([blob], file.name, { type: 'image/jpeg' }));
        }, 'image/jpeg', quality);
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });
};

export const LitterForm = ({ isOpen, onClose, onSave, editingLitter }) => {
  const [formData, setFormData] = useState({
    breed: '',
    fatherImage: '',
    motherImage: '',
    expectedDate: '',
    description: ''
  });
  const [uploading, setUploading] = useState(null); // 'father' or 'mother'

  const breedOptions = BREEDS.filter(b => b.id !== 'cesion-adulto' && b.id !== 'otras-razas');

  useEffect(() => {
    if (editingLitter) {
      setFormData({
        breed: editingLitter.breed || '',
        fatherImage: editingLitter.fatherImage || '',
        motherImage: editingLitter.motherImage || '',
        expectedDate: editingLitter.expectedDate || '',
        description: editingLitter.description || ''
      });
    } else {
      setFormData({
        breed: '',
        fatherImage: '',
        motherImage: '',
        expectedDate: '',
        description: ''
      });
    }
  }, [editingLitter, isOpen]);

  const handleImageUpload = async (e, parent) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 50 * 1024 * 1024) {
      alert('La imagen es muy grande. Maximo 50MB.');
      return;
    }
    try {
      setUploading(parent);
      const compressed = await compressImage(file, 800, 0.5);
      const imageUrl = await litterService.uploadImage(compressed);
      setFormData(prev => ({
        ...prev,
        [parent === 'father' ? 'fatherImage' : 'motherImage']: imageUrl
      }));
    } catch {
      alert('Error al subir la imagen.');
    } finally {
      setUploading(null);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      ...formData,
      id: editingLitter?.id || Date.now().toString()
    });
    onClose();
  };

  const ImageUploader = ({ label, imageKey, parentType, color }) => (
    <div className="space-y-2">
      <Label className={`${color} font-semibold`}>{label}</Label>
      {formData[imageKey] ? (
        <div className="relative">
          <img src={formData[imageKey]} alt={label} className="w-full h-36 object-cover rounded-lg" />
          <button
            type="button"
            onClick={() => setFormData({ ...formData, [imageKey]: '' })}
            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      ) : (
        <label className="border-2 border-dashed border-pink-200 rounded-lg h-36 flex flex-col items-center justify-center cursor-pointer hover:border-pink-400 transition-colors">
          {uploading === parentType ? (
            <Loader2 className="w-8 h-8 text-pink-500 animate-spin" />
          ) : (
            <>
              <Upload className="w-8 h-8 text-pink-400" />
              <span className="text-pink-600 text-sm mt-1">Subir foto</span>
            </>
          )}
          <input
            type="file"
            accept="image/*"
            onChange={(e) => handleImageUpload(e, parentType)}
            className="hidden"
            disabled={!!uploading}
          />
        </label>
      )}
    </div>
  );

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto bg-white">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-pink-900">
            {editingLitter ? 'Editar Camada' : 'Anunciar Nueva Camada'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-5 mt-4">
          {/* Breed */}
          <div className="space-y-2">
            <Label className="text-pink-800 font-semibold">Raza</Label>
            <Select value={formData.breed} onValueChange={(value) => setFormData({ ...formData, breed: value })}>
              <SelectTrigger className="border-pink-200">
                <SelectValue placeholder="Selecciona raza" />
              </SelectTrigger>
              <SelectContent>
                {breedOptions.map((breed) => (
                  <SelectItem key={breed.id} value={breed.id}>
                    {breed.icon} {breed.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Parent photos */}
          <div className="grid grid-cols-2 gap-4">
            <ImageUploader label="Foto del Papa" imageKey="fatherImage" parentType="father" color="text-blue-700" />
            <ImageUploader label="Foto de la Mama" imageKey="motherImage" parentType="mother" color="text-pink-700" />
          </div>

          {/* Expected Date */}
          <div className="space-y-2">
            <Label htmlFor="expectedDate" className="text-pink-800 font-semibold">Fecha prevista de nacimiento</Label>
            <Input
              id="expectedDate"
              type="date"
              value={formData.expectedDate}
              onChange={(e) => setFormData({ ...formData, expectedDate: e.target.value })}
              required
              className="border-pink-200 focus:border-pink-400"
            />
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="litterDesc" className="text-pink-800 font-semibold">Descripcion (opcional)</Label>
            <Textarea
              id="litterDesc"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Colores esperados, cantidad de cachorros, etc."
              rows={3}
              className="border-pink-200 focus:border-pink-400"
            />
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancelar
            </Button>
            <Button type="submit" className="bg-pink-600 hover:bg-pink-700 text-white flex-1" disabled={!!uploading}>
              {editingLitter ? 'Guardar' : 'Anunciar Camada'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
